import { EventType } from "@/analytics/analytics-events";
import { CreateAreaEventProperties } from "@/analytics/create-area-workflow-events-properties";
import {
  EmbeddedToolbar,
  EmbeddedToolbarButton,
} from "@/components/ui/embedded-toolbar";
import { HelpPopover } from "@/components/ui/help-popover";
import {
  CropIcon,
  DropHandler,
  ExportIcon,
  FaroButton,
  FaroSwitch,
  FaroText,
  FaroTextVariant,
  FaroTooltip,
  grey,
  RotateClockwiseIcon,
  TextField,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { getFileExtension } from "@faro-lotv/foundation";
import { Box, Card, Stack } from "@mui/material";
import {
  ChangeEvent,
  CSSProperties,
  DragEvent as ReactDragEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { PercentCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  ImgSheetFileExtensionsList,
  ImgSheetFileExtensionsWithDotsList,
  MAX_ALLOWED_IMG_HEIGHT,
  MAX_ALLOWED_IMG_WIDTH,
} from "./create-area-utils";
import { ImageCrop, RelativeCrop } from "./image-crop";
import {
  PageSelectorTextField,
  PageSelectorTextFieldProps,
} from "./page-selector-text-field";

interface EditImageSubmenuProps {
  isCropEnabled: boolean;

  /** Callback executed to enable/disable crop */
  onEnableCrop(isCropEnabled: boolean): void;

  /** Callback executed on click rotation button */
  onRotate(): void;

  /** if true buttons in toolbar are disabled */
  disabled: boolean;
}

/** @returns the submenu for the clip scene tool */
export function EditImageSubmenu({
  isCropEnabled,
  onEnableCrop,
  onRotate,
  disabled,
}: EditImageSubmenuProps): JSX.Element {
  return (
    <EmbeddedToolbar
      isActive
      sx={{
        backgroundColor: grey[1000],
        borderRadius: "6px",
        alignItems: "center",
        mr: "16px",
      }}
    >
      <ToolGroup orientation="vertical">
        <FaroTooltip title="Rotate image" placement="right">
          <EmbeddedToolbarButton
            size="small"
            onClick={onRotate}
            value="Rotate image"
            disabled={disabled}
            vertical
          >
            <RotateClockwiseIcon />
          </EmbeddedToolbarButton>
        </FaroTooltip>

        <FaroTooltip title="Crop image" placement="right">
          <EmbeddedToolbarButton
            size="small"
            onClick={() => {
              onEnableCrop(!isCropEnabled);
            }}
            value="Crop image"
            disabled={disabled}
            selected={isCropEnabled}
            vertical
          >
            <CropIcon />
          </EmbeddedToolbarButton>
        </FaroTooltip>
      </ToolGroup>
    </EmbeddedToolbar>
  );
}

interface OpenImageFileSelectControlProps {
  /** Callback executed on set image file */
  onSetFile(file?: File): void;
}

/** @returns control for selection file with sheet image */
export function OpenImageFileSelectControl({
  onSetFile,
}: OpenImageFileSelectControlProps): JSX.Element {
  const [fileInputEl, setFileInputEl] = useState<HTMLElement | null>(null);
  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);

  const setFileFromDrop = useCallback(
    (e: ReactDragEvent<HTMLElement>) => {
      e.preventDefault();
      // Double check that we are importing one and only one file
      if (e.dataTransfer.files.length !== 1) return;

      onSetFile(e.dataTransfer.files[0]);
    },
    [onSetFile],
  );

  const openFileExplorer = useCallback(() => {
    fileInputEl?.click();
    setIsFileExplorerOpen(true);
  }, [fileInputEl]);

  const setFileFromDialog = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsFileExplorerOpen(false);
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }

      Analytics.track<CreateAreaEventProperties>(EventType.setFloorImage, {
        via: "custom-image",
        extension: getFileExtension(file.name) ?? "",
      });

      onSetFile(file);
    },
    [onSetFile],
  );

  const closeFileDialog = useCallback(() => {
    setIsFileExplorerOpen(false);
  }, []);

  useEffect(() => {
    fileInputEl?.addEventListener("cancel", closeFileDialog);

    return () => {
      fileInputEl?.removeEventListener("cancel", closeFileDialog);
    };
  });

  return (
    <DropHandler
      sx={{
        height: "calc(100vh - 38rem)",
        maxHeight: "42.5rem",
        minHeight: "12.5rem",
        ":hover": {
          border: "solid",
          borderColor: "primary.main",
          borderRadius: "0.25rem",
        },
      }}
      onDrop={setFileFromDrop}
      canBeDropped={(e) =>
        e.dataTransfer.items.length === 1 && !isFileExplorerOpen
      }
      enabled
      onClick={openFileExplorer}
    >
      <Card
        sx={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: 1,
          boxShadow: "none",
          ":hover": {
            backgroundColor: ({ palette }) => `${palette.gray500}26`,
          },
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "3.75rem",
            height: "3.75rem",
            borderRadius: "50%",
            bgcolor: grey[100],
          }}
        >
          <ExportIcon sx={{ fontSize: "2rem" }} />
        </Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{ justifyContent: "center", alignItems: "center" }}
        >
          <FaroText variant="bodyM">Drag your data here, or&nbsp;</FaroText>
          <FaroText variant="hyperLink">
            <input
              aria-label="select sheet file"
              ref={setFileInputEl}
              type="file"
              style={{ display: "none" }}
              onChange={setFileFromDialog}
              multiple={false}
              accept={ImgSheetFileExtensionsWithDotsList.join(", ")}
            />
            <FaroText variant="hyperLink" sx={{ cursor: "pointer" }}>
              select file
            </FaroText>
          </FaroText>
          <FaroText variant="bodyM"> &nbsp;to upload</FaroText>
        </Stack>
        <FaroText variant="placeholder">
          {`Supported formats ${ImgSheetFileExtensionsList.join(", ")}`}
        </FaroText>
        <FaroText variant="placeholder">
          Maximum resolution: {MAX_ALLOWED_IMG_HEIGHT}x{MAX_ALLOWED_IMG_WIDTH}{" "}
          px
        </FaroText>
      </Card>
    </DropHandler>
  );
}

type SheetUploadControlsProps = Pick<
  PageSelectorTextFieldProps,
  "selectedPages" | "onSelect"
> & {
  /** File currently selected */
  file?: File;

  /** Indicates if the sheet upload has started */
  uploadStarted?: boolean;

  /** Indicates if the controls in the component should be disabled */
  disabled?: boolean;

  /** The validation error to show for the input name */
  inputNameError?: string;

  /** Name of the Area and ImgSheet to create */
  inputName: string;

  /** Set the name of the new area */
  onSetInputName(name: string): void;

  /** Total number of pages in PDF document; zero if file is not *.pdf */
  numberOfPdfPages?: number;

  /** Indicates if the improved resolution of the sheet should be used */
  useImprovedImageResolution: boolean;

  /** Indicates if the resolution of the sheet image can be improved */
  canImageResolutionBeImproved: boolean;

  /** Function that resets the input file and cancels the upload if the user selects so in the dialog */
  onCancelUpload(): Promise<void>;

  /** Function that creates a new Area Section, uploads the file and creates a new ImgSheet */
  onCreateSheet(): Promise<void>;

  /**
   * Callback used to indicate if the improved image resolution should be used
   *
   * @param use true if the user wants to use improved image resolution
   */
  onSetUseImprovedImageResolution(use: boolean): void;
};

/**
 * @returns Label for the "Improve resolution" switch
 */
function ImproveResolutionLabel(): JSX.Element {
  return (
    <Stack direction="row" sx={{ alignItems: "center" }}>
      <FaroText variant={FaroTextVariant.bodyM}>Improve resolution</FaroText>
      <Stack onClick={(e) => e.stopPropagation()}>
        <HelpPopover
          variant="darkInfo"
          title="Improve resolution"
          description={
            <>
              Enabling this option will generate a high resolution version of
              the floor plan.
              <br />
              <br />
              Uploading the high resolution image will take up to a few minutes.
            </>
          }
        />
      </Stack>
    </Stack>
  );
}

/** @returns control bar for setting image name, page, re-selection image and starting upload */
export function SheetUploadControls({
  file,
  uploadStarted = false,
  disabled = false,
  inputName,
  inputNameError,
  onSetInputName,
  numberOfPdfPages,
  canImageResolutionBeImproved,
  useImprovedImageResolution,
  onCancelUpload,
  onCreateSheet,
  onSetUseImprovedImageResolution,
  selectedPages,
  onSelect,
}: SheetUploadControlsProps): JSX.Element {
  const fileSettingControlsStackWidth = "70%";
  const sheetNameTextFieldWidth = "30%";

  return (
    <Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Stack
          direction="row"
          sx={{ width: fileSettingControlsStackWidth, gap: "16px" }}
        >
          <Stack sx={{ width: sheetNameTextFieldWidth }}>
            <TextField
              label="Sheet name"
              placeholder="First level"
              fullWidth
              disabled={disabled}
              sx={{
                height: "2rem",
                backgroundColor: "white",
                width: "100%",
              }}
              error={inputNameError}
              onTextChanged={onSetInputName}
              text={inputName}
              inputProps={{
                "aria-label": "sheet name",
                onBlur: () => {
                  Analytics.track<CreateAreaEventProperties>(
                    EventType.changeFloorImage,
                  );
                },
              }}
            />
          </Stack>
          {file && (
            <Stack sx={{ width: "30%", marginBottom: "3px" }}>
              {numberOfPdfPages && (
                <PageSelectorTextField
                  totalNbOfPages={numberOfPdfPages}
                  selectedPages={selectedPages}
                  onSelect={onSelect}
                />
              )}
            </Stack>
          )}
          {file && canImageResolutionBeImproved && (
            <FaroSwitch
              disabled={disabled}
              sx={{ mt: "21px", height: "2rem" }}
              label={<ImproveResolutionLabel />}
              checked={useImprovedImageResolution}
              onToggle={() => {
                onSetUseImprovedImageResolution(!useImprovedImageResolution);
              }}
            />
          )}
        </Stack>

        {file && (
          <Stack direction="row" sx={{ justifyContent: "end" }}>
            <FaroButton
              aria-label="select a different image"
              variant="secondary"
              sx={{ mt: "21px", mr: "16px", height: "2rem" }}
              disabled={disabled}
              onClick={() => {
                Analytics.track<CreateAreaEventProperties>(
                  EventType.changeFloorImage,
                );

                onCancelUpload();
              }}
            >
              Select different file
            </FaroButton>

            <FaroButton
              aria-label="upload"
              sx={{ mt: "21px", height: "2rem" }}
              disabled={disabled}
              onClick={onCreateSheet}
              isLoading={disabled}
            >
              <FaroText variant="bodyM" dark>
                {uploadStarted ? "Importing" : "Import"}
              </FaroText>
            </FaroButton>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

interface SheetImagePreviewProps {
  /** File currently selected */
  file: File;

  /** rotation angle in degrees  */
  rotation: number;

  /**
   * method to set rotation angle
   *
   * @param rotation rotation angle in degrees
   */
  setRotation(rotation: number): void;

  /**
   * method to set crop parameters
   *
   * @param rotation crop parameters (in % of original image)
   */
  setCrop(crop?: RelativeCrop): void;

  /** ratio width/height of original image - used to scale rotated image in preview  */
  widthToHeightRatio: number;

  /** Indicates if the controls in the component should be disabled */
  disabled?: boolean;

  /** The desired width of the component @default "100%" */
  width?: CSSProperties["width"];
}

// default crop at opening new file/page
const initialCrop: PercentCrop = {
  unit: "%",
  x: 30,
  y: 30,
  width: 40,
  height: 40,
};

/** @returns control to preview sheet image */
export function SheetImagePreview({
  file,
  rotation,
  setRotation,
  setCrop,
  widthToHeightRatio,
  disabled = false,
  width = "100%",
}: SheetImagePreviewProps): JSX.Element {
  const [isCropEnabled, setCropEnabled] = useState(false);

  // eslint-disable-next-line func-style -- FIXME
  const rotateImage = (): void => {
    setRotation(rotation + 90);
  };

  const scale = useMemo(() => {
    // we should never increase original size of image, only reduce if it doesn't fit to original box
    const reducedScaleFactor =
      widthToHeightRatio < 1 ? widthToHeightRatio : 1 / widthToHeightRatio;

    return rotation % 180 === 0 ? 1 : reducedScaleFactor;
  }, [rotation, widthToHeightRatio]);

  const [previewCrop, setPreviewCrop] = useState<PercentCrop | undefined>();

  const [isCropVisible, setCropVisible] = useState(false);

  // Callback used to enable/disable cropping from both: ReactCrop and buttons in parent component
  const setCropAreaDataAtTriggeringOnOff = useCallback(
    (cropEnabled: boolean) => {
      setCropVisible(cropEnabled);
      if (cropEnabled) {
        const curCrop =
          previewCrop && previewCrop.width !== 0 && previewCrop.height !== 0
            ? previewCrop
            : initialCrop;
        setCrop(curCrop);
        setPreviewCrop(curCrop);
      } else {
        setPreviewCrop(undefined);
        setCrop(undefined);
      }
    },
    [previewCrop, setCrop],
  );

  // disable crop after rotation to reset invalid previous crop
  useEffect(() => {
    setCrop(undefined);
    setPreviewCrop(undefined);
    setCropEnabled(false);
  }, [rotation, setCrop]);

  return (
    <Stack
      direction="row"
      sx={{
        width,
        bgcolor: grey[0],
        border: 3,
        borderColor: grey[300],
        borderRadius: 2,
      }}
    >
      <Stack
        sx={{ width: "100%", alignItems: "center", ml: "68px", mr: "16px" }}
      >
        <ImageCrop
          file={file}
          scale={scale}
          rotation={rotation}
          widthToHeightRatio={widthToHeightRatio}
          isCropEnabled={isCropEnabled}
          setCropEnabled={setCropEnabled}
          setCrop={setCrop}
          setPreviewCrop={setPreviewCrop}
          previewCrop={previewCrop}
          setCropVisible={setCropVisible}
          isCropVisible={isCropVisible}
        />
      </Stack>
      <Stack sx={{ justifyContent: "center" }}>
        <EditImageSubmenu
          onRotate={rotateImage}
          onEnableCrop={(e) => {
            setCropEnabled(e);
            setCropAreaDataAtTriggeringOnOff(e);
          }}
          isCropEnabled={isCropEnabled}
          disabled={disabled}
        />
      </Stack>
    </Stack>
  );
}
